import type * as THREE from 'three';
import type { CareRecord } from './care/CareRecord';
import type { DogProfile } from './dog/Profile';

/**
 * Photo mode: grab the current frame, lay a card over it (name, date, days of
 * care, how she's doing, what she's wearing), keep it in the album and offer
 * the share sheet.
 */
export interface Snapshot {
  dataUrl: string;
  at: number;
  caption: string;
}

const KEY_PREFIX = 'lunaloo.photos.';
const MAX_KEEP = 12;

export class PhotoMode {
  constructor(
    private renderer: THREE.WebGLRenderer,
    private scene: THREE.Scene,
    private camera: THREE.Camera,
    private profile: DogProfile,
    private care: CareRecord,
    private collarName: () => string,
  ) {}

  static remove(dogId: string, at: number): void {
    const list = PhotoMode.load(dogId).filter((p) => p.at !== at);
    try {
      localStorage.setItem(KEY_PREFIX + dogId, JSON.stringify(list));
    } catch {
      /* ignore */
    }
  }

  static load(dogId: string): Snapshot[] {
    try {
      return JSON.parse(localStorage.getItem(KEY_PREFIX + dogId) ?? '[]') as Snapshot[];
    } catch {
      return [];
    }
  }

  take(): Snapshot {
    // render fresh so the drawing buffer is current, then copy it out synchronously
    this.renderer.render(this.scene, this.camera);
    const src = this.renderer.domElement;
    const W = 900;
    const H = Math.round((W * src.height) / src.width);
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H + 150;
    const g = c.getContext('2d')!;
    g.fillStyle = '#fbf7f0';
    g.fillRect(0, 0, c.width, c.height);
    g.drawImage(src, 0, 0, W, H);
    // card
    const date = new Date();
    const dateStr = date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    const days = this.care.totalDays;
    const qs = this.care.qualitySeconds;
    const qt = qs >= 3600 ? `${Math.floor(qs / 3600)}h ${Math.floor((qs % 3600) / 60)}m together` : `${Math.floor(qs / 60)}m together`;
    const line2 = `${days} day${days === 1 ? '' : 's'} of care · ${qt} · ${this.care.qualityWord} · ${this.collarName()}`;
    g.fillStyle = '#2d2620';
    g.font = '600 44px -apple-system, system-ui, sans-serif';
    g.textBaseline = 'alphabetic';
    g.fillText(this.profile.name, 36, H + 62);
    g.font = '400 24px -apple-system, system-ui, sans-serif';
    g.fillStyle = '#6b6259';
    g.fillText(dateStr, 36, H + 100);
    g.fillText(line2, 36, H + 132);
    // paw
    g.fillStyle = '#7b5cc4';
    const px = W - 70;
    const py = H + 90;
    g.beginPath();
    g.ellipse(px, py + 14, 18, 14, 0, 0, Math.PI * 2);
    g.fill();
    for (const [dx, dy] of [
      [-20, -8],
      [-7, -20],
      [7, -20],
      [20, -8],
    ]) {
      g.beginPath();
      g.arc(px + dx, py + dy, 7, 0, Math.PI * 2);
      g.fill();
    }
    const dataUrl = c.toDataURL('image/jpeg', 0.86);
    const snap: Snapshot = { dataUrl, at: Date.now(), caption: `${this.profile.name} · ${dateStr} · ${line2}` };
    const list = PhotoMode.load(this.profile.id);
    list.unshift(snap);
    while (list.length > MAX_KEEP) list.pop();
    try {
      localStorage.setItem(KEY_PREFIX + this.profile.id, JSON.stringify(list));
    } catch {
      /* storage full: keep in memory only */
    }
    return snap;
  }

  /** the phone's share sheet when available, else a download */
  async share(snap: Snapshot): Promise<boolean> {
    try {
      const blob = await (await fetch(snap.dataUrl)).blob();
      const file = new File([blob], `${this.profile.id}-${new Date(snap.at).toISOString().slice(0, 10)}.jpg`, { type: 'image/jpeg' });
      const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
      if (nav.share && (!nav.canShare || nav.canShare({ files: [file] }))) {
        await nav.share({ files: [file], title: this.profile.name, text: snap.caption });
        return true;
      }
      const a = document.createElement('a');
      a.href = snap.dataUrl;
      a.download = file.name;
      a.click();
      return true;
    } catch {
      return false;
    }
  }
}
